import type { WorkOrder } from '../../types/workOrder';
import type { InventoryItemFormData } from '../../types/inventory';
import { workOrderService } from '../workOrderService';
import { inventoryItemsService } from './inventoryItemsService';

const resolveBatchCogs = (cogs?: {
  total_input_cost?: number;
  unit_cost?: number;
  output_quantity?: number;
  standard_cost?: number;
}) => {
  if (!cogs) {
    return 0;
  }

  if (typeof cogs.total_input_cost === 'number' && Number.isFinite(cogs.total_input_cost) && cogs.total_input_cost > 0) {
    return cogs.total_input_cost;
  }

  if (typeof cogs.standard_cost === 'number' && Number.isFinite(cogs.standard_cost) && cogs.standard_cost > 0) {
    return cogs.standard_cost;
  }

  if (
    typeof cogs.unit_cost === 'number' &&
    Number.isFinite(cogs.unit_cost) &&
    cogs.unit_cost > 0 &&
    typeof cogs.output_quantity === 'number' &&
    Number.isFinite(cogs.output_quantity) &&
    cogs.output_quantity > 0
  ) {
    return cogs.unit_cost * cogs.output_quantity;
  }

  if (typeof cogs.unit_cost === 'number' && Number.isFinite(cogs.unit_cost) && cogs.unit_cost > 0) {
    return cogs.unit_cost;
  }

  return 0;
};

export const inventoryWorkOrderService = {
  createFromWorkOrder: async (orderId: string, existingOrder?: WorkOrder, expiryDate?: string): Promise<void> => {
    try {
      const order = existingOrder || (await workOrderService.getById(orderId));
      if (!order) {
        console.error('Work order not found for auto-creation:', orderId);
        return;
      }

      const quantity = order.outputWeight ?? 0;
      if (quantity <= 0) {
        console.warn('Work order has no output weight, skipping inventory creation:', orderId);
        return;
      }

      let existingItemId: string | null = null;
      let existingItemData: InventoryItemFormData | null = null;
      try {
        const allItems = await inventoryItemsService.getAll();
        const existing = allItems.find(i => i.workOrderId === orderId);
        if (existing) {
          existingItemId = existing.id;
          existingItemData = {
            name: existing.name,
            code: existing.code,
            description: existing.description,
            unitOfMeasure: existing.unitOfMeasure,
            itemType: existing.itemType,
            reorderPoint: existing.reorderPoint ?? 0,
            reorderQuantity: existing.reorderQuantity ?? 0,
            standardCost: existing.standardCost ?? 0,
            quantity,
            expiryDate: existing.expiryDate,
            workOrderId: existing.workOrderId,
            status: existing.status,
            location: existing.location,
            greenBeanProfileId: existing.greenBeanProfileId,
            packSizeGrams: existing.packSizeGrams,
          };
        }
      } catch (error) {
        console.warn('Failed to check for existing items, proceeding with creation risk:', error);
      }

      let standardCost = 0;
      let roastTotalInputCost = 0;

      try {
        const cogs = await workOrderService.getRoastCogs(order.id);
        const batchCost = resolveBatchCogs(cogs);
        if (batchCost > 0) {
          roastTotalInputCost = batchCost;
          standardCost = batchCost;
        }
      } catch (error) {
        console.error('Failed to get COGS during auto-creation', error);
      }

      const woNumber = order.workOrderNumber || order.id;
      const baseName = `Roasting ${woNumber}`;

      const basePayload: InventoryItemFormData = {
        name: baseName,
        code: undefined,
        itemType: 'roasted_bulk',
        workOrderId: order.id,
        standardCost: standardCost,
        unitOfMeasure: 'kg',
        quantity: quantity,
        description: `Produced from Work Order ${woNumber}`,
        roastInputWeight: order.inputWeight ?? 0,
        roastTotalInputCost: roastTotalInputCost,
        reorderPoint: 0,
        reorderQuantity: 0,
        expiryDate: expiryDate || undefined,
      };

      if (existingItemId && existingItemData) {
        const updatePayload: InventoryItemFormData = {
          ...existingItemData,
          standardCost,
          quantity,
          roastInputWeight: order.inputWeight ?? 0,
          roastTotalInputCost: roastTotalInputCost || standardCost,
          expiryDate: expiryDate || existingItemData.expiryDate,
        };
        await inventoryItemsService.update(existingItemId, updatePayload);
        return;
      }

      await inventoryItemsService.create(basePayload);
    } catch (error) {
      console.error('Failed to auto-create inventory item from work order', error);
      throw error;
    }
  },
};
